import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { ReprocessCompletionsRequest } from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import Field from "components/Field";
import { Guid } from "guid-typescript";

const parseCompletionIds = (input: string): string[] => {
  // Split by space, comma, semicolon, or newline
  const delimiters = /[\s,;\n]+/;
  return input?.split(delimiters)
    .map((id) => id.trim())
    .filter((id) => id.length > 0) ?? [];
};

type FormValues = {
  completionIds: string;
  changeReason: string;
  sendEmails: boolean;
};

const ReprocessCompletionsPage = () => {
  const { formatMessage: t } = useIntl();
  useTitle(t({ id: "reprocessCompletions" }));
  const { completionService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);


  const validateForm = (values: FormValues) => {
    const errors: Partial<FormValues> = {};
    const completionIds = parseCompletionIds(values.completionIds);
    if (completionIds.length === 0) {
      errors.completionIds = t({ id: "requiredLabel" });
    }

    if(completionIds.some(id => !Guid.isGuid(id))) {
      errors.completionIds = t({ id: "invalidGuid" });
    }

    return errors;
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const completionIds = parseCompletionIds(values.completionIds);

      const request = ReprocessCompletionsRequest.fromJS({
        userDeliverablesCompletionIds: completionIds,
        changeReason: values.changeReason,
        sendEmails: values.sendEmails,
      });

      await completionService.reprocess(request);

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "reprocessCompletionsSuccess" }, { count: completionIds.length })
        )
      );

    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const initialValues = useMemo<FormValues>(
    () => ({
      completionIds: "",
      changeReason: "",
      sendEmails: true,
    }),
    []
  );

  return (
    <Box my={4}>
      <Typography variant="h5" gutterBottom>
        <T id="reprocessCompletions" />
      </Typography>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Form
          onSubmit={onSubmit}
          validate={validateForm}
          initialValues={initialValues}
          render={({ handleSubmit, submitting, submitError, form, submitSucceeded }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field name="completionIds" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        fullWidth
                        disabled={submitting || submitSucceeded}
                        multiline
                        rows={10}
                        required
                        label={t({ id: "userDeliverableCompletionIdsLabel" })}
                        variant="outlined"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="changeReason" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        fullWidth
                        disabled={submitting || submitSucceeded}
                        label={t({ id: "changeReasonLabel" })}
                        variant="outlined"
                        required
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="sendEmails" type="checkbox">
                    {({ input }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...input}
                            disabled={submitting || submitSucceeded}
                          />
                        }
                        label={t({ id: "sendEmailsLabel" })}
                      />
                    )}
                  </Field>
                </Grid>

                {submitError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{submitError}</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" gap={2} alignItems="center">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting || submitSucceeded}
                      startIcon={submitting && <CircularProgress size={16} />}
                    >
                      {t({ id: "processButton" })}
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      color="secondary"
                      disabled={submitting}
                      onClick={() => form.restart()}
                    >
                      {t({ id: "clearButton" })}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </Paper>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default ReprocessCompletionsPage;
